/**
 * 261023 오류 자동 수집 — Sentry(오류·실패한 요청·'오류 신고' 버튼·오류 직전 화면 기록) + Clarity(전체 화면 기록·연타 감지).
 *   유저 "경우의 수가 보통이 아닌데 테스트를 깊이 할 시간이 없다" → 실제 사용이 테스트를 대신하게 한다.
 *   오류마다 '어느 경우였나'(들어온 길·기기·앱 버전·로그인·화면 폭·화면 경로)를 태그로 붙여 재현 없이 좁힌다.
 *   아침 분류·자동 수정은 PC 쪽이 Sentry API 로 읽어서 한다(이 파일은 모으기만).
 *
 * 켜지는 곳 = 주소가 ENV_BY_HOST 에 있고 Env 의 SENTRY_DSN·CLARITY_ID 가 있을 때만(테섭 jump-dev.site · 운영 user·ganpanon.com).
 *   운영은 처리방침에 국외 이전(Sentry·Microsoft) 문구를 넣은 뒤 Env_prod 에 값을 넣고 ENV_BY_HOST 에 운영 주소를 연다.
 * SDK 는 npm 이 아니라 CDN(버전·SRI 고정)으로 받는다 — 공유 테섭의 node_modules 를 건드리지 않고 앱 번들 크기도 그대로.
 */
import { CODE_ENV_LIST } from '@/common/constants/ConstCode';
import store from '@/store';
import { isApp } from '@/common/utils/module/IsMobileSupport';
import { isKakaoInApp, isInAppBrowser } from '@/common/utils/module/InAppBrowser';

const SENTRY_SDK_URL = 'https://browser.sentry-cdn.com/10.76.2/bundle.replay.feedback.min.js';
const SENTRY_SDK_SRI = 'sha384-hkME2xc808Ywqa17wT1H1ongRzDRIS5tDz+IyCtVEIrDNv2DqIwVqDm2+8SYsrIP';
const ENV_BY_HOST = {
  'jump-dev.site': 'testbed',
  'user.xn--hy1bu3cba253loncda414vea.com': 'production',
  'ganpanon.com': 'production',
  'www.ganpanon.com': 'production'
};
// 운영은 처리방침에 국외 이전(Sentry·Microsoft) 문구가 들어가기 전까지 개인을 알아볼 수 있는 건 안 보낸다 —
// 회원 번호 없음·화면 기록의 글자·사진 가림. '오류 신고' 버튼은 테섭에서 모양을 본 뒤 운영에도 띄움(261023 유저 '운영에 올려줘') → 운영은 최 팀장만(아래)
const isTestbed = () => env === 'testbed';

// '오류 신고' 버튼 — 운영은 최 팀장(236) 한 명에게만(261026 대표 회의 '너만 해 놔' — 대표님이 손님에게 화면을 보여 줄 때 자꾸 눌림), 테섭은 그대로 모두.
//   261023 처음엔 가입 알림 받는 운영진 4명(UserService.NEW_MEMBER_WATCH_PHONES)의 운영 계정 78·80·86·236 이었다.
//   전화번호를 화면 코드에 싣지 않으려고 회원 번호로 둔다 — 다른 계정을 쓰게 되면 여기도 바꾼다. 오류 자동 수집은 손님 포함 그대로.
const OPERATOR_USER_NOS = [236];
const canReport = () => isTestbed() || OPERATOR_USER_NOS.includes(Number(store.getters['user/getUserData']?.userNo));

// 고칠 게 없는 소음 — 브라우저 내부 경고·확장 프로그램·남의 스크립트
const IGNORE_ERRORS = ['ResizeObserver loop', 'Non-Error promise rejection captured', 'Script error.'];
const DENY_URLS = [
  /clarity\.ms/i,
  /kakao(cdn)?\.(com|net)/i,
  /daumcdn\.net/i,
  /static\.nid\.naver\.com/i,
  /accounts\.google\.com/i,
  /^(chrome|moz|safari)-extension:/i
];

// 같은 실패가 화면에서 반복될 때(재시도·목록 반복 호출) 30초에 한 번만 — 무료 한도(월 건수) 보호
const REPEAT_GAP_MS = 30 * 1000;

const FEEDBACK_OPTIONS = {
  colorScheme: 'light',
  showBranding: false,
  showName: false,
  showEmail: false,
  enableScreenshot: true,
  triggerLabel: '오류 신고',
  triggerAriaLabel: '오류 신고',
  formTitle: '오류 신고',
  messageLabel: '무엇이 안 됐습니까?',
  messagePlaceholder: '예: 저장 버튼을 눌러도 아무 반응이 없습니다.',
  isRequiredLabel: '(필수)',
  addScreenshotButtonLabel: '지금 화면 캡처 넣기',
  removeScreenshotButtonLabel: '캡처 빼기',
  confirmButtonLabel: '확인',
  submitButtonLabel: '보내기',
  cancelButtonLabel: '닫기',
  successMessageText: '보내 주셔서 감사합니다. 확인해서 고치겠습니다.'
};

let env = null;
let sentryReady = false;
const pending = []; // SDK 를 받기 전에 난 오류 — 받으면 한꺼번에 보낸다(최대 20)
const lastSent = new Map();

// 개인정보가 섞일 수 있는 경로 조각(이메일·전화번호 같은 긴 숫자·한글·인코딩된 글자)은 가리고 ? 뒤는 버린다
const safePath = path =>
  String(path || '')
    .split(/[?#]/)[0]
    .split('/')
    .map(seg => (/[@%]|[^\x00-\x7F]|\d{9,}/.test(seg) ? ':pii' : seg))
    .join('/');

// 경로 속 번호·코드는 ':id' 로 묶는다 — '/s/P00001'·'/business/detail/123' 이 코드마다 따로 잡히지 않게
const pathPattern = path =>
  safePath(path)
    .split('/')
    .map((seg, i, all) =>
      /^\d+$/.test(seg) || (/^[A-Z0-9-]{5,}$/.test(seg) && /\d/.test(seg)) || seg.length > 24 || (all[i - 1] === 's' && seg)
        ? ':id'
        : seg
    )
    .join('/') || '/';

// 보고에 싣는 주소 — 운영은 QR·번호까지 ':id' 로 묶는다(주차판 코드는 차 주인과 이어진다). 테섭은 재현용으로 코드를 남긴다.
const reportPath = path => (isTestbed() ? safePath(path) : pathPattern(path));

// 오류 직전 기록(요청·화면 이동)도 같은 기준으로 — 콘솔 로그는 기기 정보·푸시 토큰이 찍혀서 아예 안 보낸다
const scrubBreadcrumb = crumb => {
  if (crumb.category === 'console') return null;
  if (crumb.data?.url) crumb.data.url = reportPath(crumb.data.url);
  if (crumb.data?.from) crumb.data.from = reportPath(crumb.data.from);
  if (crumb.data?.to) crumb.data.to = reportPath(crumb.data.to);
  return crumb;
};

const widthBucket = w => (w < 340 ? '~339' : w < 360 ? '340~359' : w < 390 ? '360~389' : w < 430 ? '390~429' : '430~');

const caseTags = () => {
  const ua = navigator.userAgent;
  const device = store.getters['user/getDeviceInfo'] || {};
  return {
    entry: isApp() ? 'app' : isKakaoInApp() ? 'kakao_inapp' : isInAppBrowser() ? 'other_inapp' : 'browser',
    platform: /android/i.test(ua) ? 'android' : /iphone|ipad|ipod/i.test(ua) ? 'ios' : 'pc',
    app_version: (isApp() && device.appVersion) || '-', // 브라우저는 기본값 1.0.0 이 들어 있어서 앱일 때만
    login: store.getters['user/getAccessToken'] ? 'yes' : 'no',
    role: store.getters['user/getRole'] || '-',
    width: widthBucket(window.innerWidth),
    page: pathPattern(window.location.pathname),
    // 자동 시험(Playwright 등)이 낸 오류 표시 — 아침 분류·자동 수정이 사람이 겪은 것부터 보게
    automation: navigator.webdriver ? 'yes' : 'no'
  };
};

const syncScope = S => {
  S.setTags(caseTags());
  const authNo = isTestbed() ? store.getters['user/getUserData']?.authNo : null;
  S.setUser(authNo ? { id: String(authNo) } : null);
};

const syncClarityTags = () => {
  if (!window.clarity) return;
  Object.entries(caseTags()).forEach(([key, value]) => window.clarity('set', key, value));
};

const capture = fn => {
  if (!env) return;
  if (sentryReady) {
    syncScope(window.Sentry);
    fn(window.Sentry);
  } else if (pending.length < 20) {
    pending.push(fn);
  }
};

let feedbackButton = null;
let feedbackRoot = null;
let feedbackObserver = null;
let feedbackFrame = 0;
let feedbackTimers = [];

// 확인 창·모달이 떠 있으면 버튼을 숨긴다 — '현판 사용법'도 그때 사라지고, 남은 버튼만 어두운 막 위로 밝게 뜨면 어색하다(pc-6d 지적)
const modalOpen = () =>
  (store.getters['layout/getModalComponents'] || []).length > 0 || !!document.querySelector('.modal-dim.on');

const positionFeedbackButton = () => {
  if (!feedbackButton) return;
  // 운영진이 로그아웃해 같은 기기를 다른 계정이 이어 써도 숨긴다
  if (modalOpen() || !canReport()) feedbackButton.style.setProperty('display', 'none', 'important');
  else feedbackButton.style.removeProperty('display');
  const root = document.querySelector('.gp');
  if (root !== feedbackRoot) {
    feedbackObserver?.disconnect();
    feedbackRoot = root;
    if (root) {
      // 홈 직계 자식만 관찰해 모달과 회원 버튼 전환을 따라간다. 본문 전체는 감시하지 않는다.
      feedbackObserver = new MutationObserver(queueFeedbackPosition);
      feedbackObserver.observe(root, { childList: true });
    }
  }
  let top = Infinity;
  let anchor = null;
  document.querySelectorAll('.gp-guide-fab, .gp-member-fab').forEach(fab => {
    const rect = fab.getBoundingClientRect();
    const style = window.getComputedStyle(fab);
    if (rect.width && rect.height && rect.bottom > 0 && rect.top < window.innerHeight &&
        style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
      // fixed 버튼의 원래 위치로 재야 떠오른 만큼 간격이 벌어지지 않는다.
      if (fab.offsetTop < top) {
        top = fab.offsetTop;
        anchor = fab;
      }
    }
  });
  // 버튼이 없으면 CSS에 지정한 현판 사용법의 기본 하단 위치로 돌아간다.
  if (top === Infinity) feedbackButton.style.removeProperty('bottom');
  else feedbackButton.style.setProperty('bottom', `${window.innerHeight - top + 10}px`, 'important');
  if (typeof anchor?.getAnimations === 'function' && typeof feedbackButton.getAnimations === 'function') {
    const reference = anchor.getAnimations().find(animation =>
      animation.animationName?.startsWith('gpGuideFabFloat') && animation.playState === 'running');
    const floating = feedbackButton.getAnimations().find(animation => animation.animationName === 'gpErrorFeedbackFloat');
    if (reference && floating && reference.currentTime !== null) {
      floating.currentTime = reference.currentTime;
    }
  }
};

const queueFeedbackPosition = () => {
  if (!feedbackButton || feedbackFrame) return;
  feedbackFrame = window.requestAnimationFrame(() => {
    feedbackFrame = 0;
    positionFeedbackButton();
  });
};

const scheduleFeedbackPosition = () => {
  if (!feedbackButton) return;
  feedbackTimers.forEach(timer => window.clearTimeout(timer));
  queueFeedbackPosition();
  // Vue 렌더와 회원 정보 요청이 늦게 끝나는 경우를 위해 한정된 횟수만 재측정한다.
  feedbackTimers = [300, 1200, 3000].map(delay => window.setTimeout(queueFeedbackPosition, delay));
};

const createFeedbackButton = () => {
  if (!env || feedbackButton || !canReport()) return;
  const feedback = window.Sentry.getFeedback?.();
  if (!feedback) return;
  const style = document.createElement('style');
  style.textContent = `
    #gp-error-feedback {
      position:fixed!important; top:auto!important; left:auto!important;
      right:36px!important; bottom:calc(72px + env(safe-area-inset-bottom))!important;
      width:44px!important; height:44px!important; min-width:0!important; min-height:0!important;
      max-width:none!important; max-height:none!important; padding:0!important; margin:0!important;
      border:1px solid #0f1f3d40!important; border-radius:50%!important; box-sizing:border-box!important;
      font:normal 20px/1 sans-serif!important; background:#fff!important; color:#0F1F3D!important;
      display:flex!important; align-items:center!important; justify-content:center!important;
      box-shadow:0 3px 10px #0f1f3d26!important; z-index:60!important; cursor:pointer!important;
      appearance:none!important; animation:gpErrorFeedbackFloat 2.6s ease-in-out infinite;
    }
    @keyframes gpErrorFeedbackFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-5px); } }
    @media(prefers-reduced-motion:reduce) { #gp-error-feedback { animation:none!important; } }
    #gp-error-feedback:focus-visible { outline:2px solid #0F1F3D!important; outline-offset:3px!important; }
    @media(max-width:360px) { #gp-error-feedback { width:40px!important; height:40px!important; right:30px!important; } }
    @media(min-width:768px) { #gp-error-feedback { width:48px!important; height:48px!important; right:50px!important; bottom:28px!important; } }
  `;
  document.head.appendChild(style);
  feedbackButton = document.createElement('button');
  feedbackButton.id = 'gp-error-feedback';
  feedbackButton.type = 'button';
  feedbackButton.setAttribute('aria-label', '오류 신고');
  feedbackButton.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" style="width:22px;height:22px;display:block;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6m0 4h.01"/></svg>';
  feedback.attachTo(feedbackButton);
  document.body.appendChild(feedbackButton);
  window.addEventListener('resize', scheduleFeedbackPosition, { passive: true });
  store.watch(() => (store.getters['layout/getModalComponents'] || []).length, scheduleFeedbackPosition);
  scheduleFeedbackPosition();
};

const loadSentry = dsn => {
  const script = document.createElement('script');
  script.src = SENTRY_SDK_URL;
  script.integrity = SENTRY_SDK_SRI;
  script.crossOrigin = 'anonymous';
  script.async = true;
  script.onload = () => {
    const S = window.Sentry;
    if (!S?.init) return;
    S.init({
      dsn,
      environment: env,
      sendDefaultPii: false,
      integrations: [
        // 오류가 나면 그 직전 화면을 같이 보낸다. 입력칸은 항상 가린다(전화번호·비번). 글자·사진은 테섭에서만 보인다.
        S.replayIntegration({ maskAllText: !isTestbed(), maskAllInputs: true, blockAllMedia: !isTestbed() }),
        S.feedbackIntegration({ ...FEEDBACK_OPTIONS, autoInject: false })
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      beforeBreadcrumb: scrubBreadcrumb,
      beforeSend: event => {
        if (event.request?.url) event.request.url = window.location.origin + reportPath(event.request.url.replace(window.location.origin, ''));
        // Clarity 쪽에서 이 오류가 난 세션을 찾을 수 있게 표시(필터: 사용자 지정 태그 sentry_event)
        if (window.clarity) {
          window.clarity('set', 'sentry_event', event.event_id);
          window.clarity('event', 'sentry_error');
        }
        return event;
      }
    });
    syncScope(S);
    sentryReady = true;
    pending.splice(0).forEach(fn => fn(S));
    createFeedbackButton();
    // 운영진이 나중에 로그인하면 그때 만들고, 로그아웃하면 숨긴다
    store.watch(() => store.getters['user/getUserData']?.userNo, () => {
      createFeedbackButton();
      scheduleFeedbackPosition();
    });
  };
  document.head.appendChild(script);

};

const loadClarity = id => {
  // Clarity 공식 설치 코드와 같은 모양 — 스크립트가 오기 전 호출은 큐(clarity.q)에 쌓였다가 넘어간다
  window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${id}`;
  document.head.appendChild(script);
  syncClarityTags();
};

export const initErrorMonitor = (app, router) => {
  env = ENV_BY_HOST[window.location.hostname] || null;

  // 컴포넌트 안에서 난 오류는 Vue 가 받아서 window 'error' 로 안 올라온다 → 여기서 받는다.
  // 원래 동작은 그대로 — 개발 빌드(테섭)는 Vue 가 다시 던지고(콘솔 빨간 줄·E2E pageerror), 운영 빌드는 콘솔에만 찍는다.
  app.config.errorHandler = (err, instance, info) => {
    capture(S =>
      S.withScope(scope => {
        scope.setTag('kind', 'vue');
        scope.setContext('vue', { component: instance?.$options?.name || instance?.$options?.__name || '', info });
        S.captureException(err);
      })
    );
    if (process.env.NODE_ENV !== 'production') throw err;
    console.error(err);
  };

  if (!env) return;
  if (CODE_ENV_LIST.SENTRY_DSN) loadSentry(CODE_ENV_LIST.SENTRY_DSN);
  if (CODE_ENV_LIST.CLARITY_ID) loadClarity(CODE_ENV_LIST.CLARITY_ID);
  // 화면을 옮길 때마다 태그를 새로 — '오류 신고' 버튼으로 보낸 글도 지금 화면 경로가 붙게
  router.afterEach(() => {
    syncClarityTags();
    if (sentryReady) syncScope(window.Sentry);
    scheduleFeedbackPosition();
  });
};

/**
 * 서버 요청 실패(ServiceExec.handleError) — 사용자 눈에 '오류'로 보인 순간. 401 은 로그인 만료라 뺀다.
 * status 0 = 응답 없음(끊김·시간 초과·주소에 undefined 가 낀 요청 차단).
 */
export const reportApiFailure = error => {
  const status = error?.response?.status || 0;
  if (!env || status === 401) return;
  const config = error?.config || {};
  const method = String(config.method || 'get').toUpperCase();
  const api = config.url ? pathPattern(config.url) : error?.code || 'unknown';
  const key = `${method} ${api} ${status}`;
  const now = Date.now();
  if (now - (lastSent.get(key) || 0) < REPEAT_GAP_MS) return;
  lastSent.set(key, now);
  const body = error?.response?.data;
  capture(S =>
    S.captureMessage(`API ${status || 'NO_RESPONSE'} ${method} ${api}`, {
      level: status >= 500 || status === 0 ? 'error' : 'warning',
      tags: { kind: 'api_fail', api, api_status: String(status) },
      extra: {
        url: reportPath(config.url),
        code: error?.code,
        message: error?.message,
        serverMessage: typeof body === 'string' ? body.slice(0, 300) : undefined
      },
      fingerprint: ['api-fail', method, api, String(status)]
    })
  );
};

/**
 * 261023 아이폰 주차판 '로그인 안 뜸' 재발 감시(App.vue 이어서 등록 안전망) — 앱이 맡긴 주차판 코드로 등록(로그인) 화면을 열었는데
 *   등록 없이 홈에 닿은 순간 한 건. 이동 기록(trail)으로 '<'·스와이프로 뒤로 갔는지('/signin' → '/')·홈이 덮었는지('/' → '/intro')를 가른다.
 *   덧붙이는 값(extra)엔 코드·회원을 넣지 않는다(경로 속 코드는 :id, 쿼리는 버림). 자동으로 붙는 요청 기록은 위 scrubBreadcrumb 규칙 그대로.
 */
export const reportQrResumeMiss = ({ seconds = 0, trail = [] } = {}) => {
  capture(S =>
    S.captureMessage('QR 이어가기 — 등록 없이 홈 도착', {
      level: 'warning',
      tags: { kind: 'qr_resume_miss' },
      extra: { seconds, trail: trail.map(pathPattern) },
      fingerprint: ['qr-resume-miss']
    })
  );
};
